import { z } from "zod";

import { OrderDocumentType } from "@/src/models/constants/document-types";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { locationSchema } from "@/src/models/location";
import { patientMinimalSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const orderTypeDisplay: Record<OrderDocumentType, string> = {
  [OrderDocumentType.CARDIAC]: "Cardiac Order",
  [OrderDocumentType.CLARIFICATION]: "Order Clarification",
  [OrderDocumentType.IMAGING]: "Imaging Order",
  [OrderDocumentType.LAB]: "Lab Order",
  [OrderDocumentType.PROCEDURE]: "Procedure Order",
  [OrderDocumentType.SIGNED]: "Signed Order",
};

const extendedOrderSchema = z.object({
  ...DocumentDateSchema.shape,
  patient: patientMinimalSchema,
  provider: practitionerSchema.nullable(),
  receivingProvider: practitionerSchema.nullable(),
  location: locationSchema.nullable(),
  receivingLocation: locationSchema.nullable(),
});

const orderCustomFields: Record<OrderDocumentType, Record<string, z.ZodTypeAny>> = {
  [OrderDocumentType.CARDIAC]: extendedOrderSchema.shape,
  [OrderDocumentType.CLARIFICATION]: extendedOrderSchema.shape,
  // Imaging orders name the modality being ordered; groups render it in the document label.
  [OrderDocumentType.IMAGING]: { ...extendedOrderSchema.shape, imagingType: z.string().nullable() },
  [OrderDocumentType.LAB]: extendedOrderSchema.shape,
  [OrderDocumentType.PROCEDURE]: extendedOrderSchema.shape,
  [OrderDocumentType.SIGNED]: extendedOrderSchema.shape,
};

const makeOrderSchema = (type: OrderDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...customFields,
  });

export const orderSchemas: Record<OrderDocumentType, ReturnType<typeof makeOrderSchema>> = (
  Object.values(OrderDocumentType) as OrderDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeOrderSchema(type, orderCustomFields[type]);

    return acc;
  },
  {} as Record<OrderDocumentType, ReturnType<typeof makeOrderSchema>>
);

const orderSchemasArray = (Object.values(OrderDocumentType) as OrderDocumentType[]).map(
  (type) => orderSchemas[type]
) as [ReturnType<typeof makeOrderSchema>, ...ReturnType<typeof makeOrderSchema>[]];

export const anyOrderSchema = z.discriminatedUnion("type", orderSchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type Order = ExtractedDocumentFields<OrderDocumentType, typeof extendedOrderSchema> & DatabaseDefaults;

export const orderShape: Order = {
  id: "",
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  groupId: "",
  type: OrderDocumentType.LAB,
  receivingLocation: null,
  receivingProvider: null,
  dateOfDocument: null,
  dateReceived: null,
  dateSigned: null,
  dateOfEvent: null,
};
